import { alpha } from '@material-ui/core/styles';
import { styledMargin } from '@tink/core/src/components/atoms/Margins/margins';
import { css, styled } from '../../theme/styled';
import { Caption } from '../../../components/atoms/Text/Text';
import { H4 } from '../../../components/atoms/Heading/Heading';

export const EmptyResult = styled.div`
  color: ${(props) => props.theme.configuration.secondaryTextColor};
  margin-top: ${(props) => props.theme.sizes.medium};
  text-align: left;
`;

export const StyledButton = styled.button`
  width: 100%;
  height: 100%;
  display: flex;
  padding: 0;
  align-items: center;
  background: transparent;
  border: 0;
  font-family: ${(props) => props.theme.font.face};
  filter: ${(props) => (props.disabled ? 'grayscale(100%)' : 'none')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    cursor: pointer;
  }
`;

export const CantFindYourBankListItem = styled.li`
  background-color: transparent;
  margin-top: ${(props) => props.theme.sizes.xxlarge};
  margin-bottom: ${(props) => props.theme.sizes.small};
  ${StyledButton} {
    justify-content: center;
    color: ${(props) => props.theme.configuration.primaryColor};
    font-weight: ${(props) => props.theme.font.weights.semiBold};
    font-size: ${(props) => props.theme.sizes.normal};
  }
`;

export const ProvidersListWrapper = styled.div`
  overflow-y: auto;
  flex-shrink: 1;
  margin-left: -${(props) => props.theme.sizes.normal};
  margin-right: -${(props) => props.theme.sizes.normal};
  padding: 0 ${(props) => props.theme.sizes.normal};

  ${CantFindYourBankListItem} {
    @media ${(props) => props.theme.device.tablet} {
      display: none;
    }
  }
`;

export const StyledStateButton = styled(StyledButton)`
  border-bottom: 1px solid ${(props) => props.theme.configuration.strokeColor};
`;

export const StyledStateContainer = styled.span`
  text-align: left;
  padding: ${(props) => props.theme.sizes.medium} 0;
  @media ${({ theme }) => theme.device.tablet} {
    text-align: center;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const ListItem = styled.li`
  background-color: transparent;
  margin-bottom: ${(props) => props.theme.sizes.small};
`;

export const StateListItem = styled(ListItem)`
  @media ${({ theme }) => theme.deviceMaxWidth.tablet} {
    margin-bottom: 0;
    &:last-child ${StyledStateButton} {
      border-bottom: none;
    }
  }
`;

export const StyledTagConnectedMobile = styled.span`
  background-color: ${({ theme }) => theme.configuration.tintedBackgroundColor};
  color: ${({ theme }) => theme.configuration.secondaryTextColor};
  padding: ${({ theme }) => theme.sizes.xxsmall};
  border-radius: ${({ theme }) => theme.roundedCorners.small};
  margin-left: auto;
`;

export const StyledTagConnectedDesktop = styled.span`
  background-color: ${({ theme }) => theme.configuration.tintedBackgroundColor};
  color: ${({ theme }) => theme.configuration.secondaryTextColor};
  padding: ${({ theme }) => theme.sizes.xxsmall};
  border-radius: ${({ theme }) => theme.roundedCorners.small};
  margin-top: -28px;
  margin-left: -60%;
  display: none;
`;

export const StyledProviderIcon = styled.img`
  ${styledMargin} {
    width: ${(props) => props.theme.sizes.xlarge};
    object-fit: contain;
  }
`;

export const CantFindYourBankIcon = styled.div`
  width: ${(props) => props.theme.sizes.xlarge};
  border-radius: ${({ theme }) => theme.roundedCorners.huge};
  background-color: ${({ theme }) => theme.configuration.tintedBackgroundColor};
`;

export const List = styled.ul`
  display: block;
  height: 100%;
  list-style: none;
  margin: 0;
  flex-grow: 1;
  overflow-y: auto;
  padding-bottom: ${(props) => props.theme.sizes.small};
  padding-left: 0;

  ${StyledProviderIcon}, ${CantFindYourBankIcon} {
    margin-right: ${(props) => props.theme.sizes.xsmall};
  }

  ${({ theme }) =>
    css`
      @media ${theme.device.tablet} {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        grid-gap: ${theme.sizes.normal};
        padding: 0.25rem 0.25rem 3rem;
        margin-left: -0.25rem;
        margin-right: -0.25rem;

        ${ListItem} {
          margin-bottom: 0;
        }

        ${StyledButton} {
          flex-direction: column;
          justify-content: flex-end;
          min-height: ${theme.sizes.xlarge};
          padding: ${theme.sizes.xsmall};
          border: 1px solid ${theme.configuration.strokeColor};
          border-radius: ${theme.roundedCorners.medium};

          &:hover {
            border-color: ${theme.configuration.primaryColor};
          }

          &:active,
          &:focus {
            box-shadow: 0 0 0 3px ${alpha(theme.configuration.primaryColor, 0.25)};
          }
        }

        ${StyledStateButton} {
          justify-content: center;
          min-height: 6rem;
        }

        ${StyledProviderIcon}, ${CantFindYourBankIcon} {
          margin: auto auto ${theme.sizes.xsmall};
        }

        ${StyledTagConnectedMobile} {
          margin: auto auto ${theme.sizes.xsmall};
          display: none;
        }

        ${StyledTagConnectedDesktop} {
          display: block;
        }

        ${Caption} {
          max-width: 100%;
        }
      }
    `}
`;

export const StyledProviderContainer = styled.span`
  text-align: left;
  @media ${({ theme }) => theme.device.tablet} {
    text-align: center;
    flex: 1;
  }
`;

export const StyledImageContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export const StyledImage = styled.img`
  max-width: 260px;
  margin-top: ${({ theme }) => theme.sizes.medium};
  @media ${({ theme }) => theme.device.mobileL} {
    max-width: 340px;
  }
`;

export const StyledTag = styled.span`
  background-color: ${({ theme }) => theme.configuration.tintedBackgroundColor};
  color: ${({ theme }) => theme.configuration.secondaryTextColor};
  padding: ${({ theme }) => theme.sizes.xxsmall};
  border-radius: ${({ theme }) => theme.roundedCorners.small};
`;

export const ListHeader = styled(H4)`
  border-bottom: 1px solid ${({ theme }) => theme.configuration.strokeColor};
  font-weight: ${({ theme }) => theme.font.weights.regular};
  color: ${({ theme }) => theme.configuration.secondaryTextColor};
  font-size: ${({ theme }) => theme.sizes.xsmall};
  line-height: ${({ theme }) => theme.sizes.medium};
`;
